import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { AlertModal } from '@/components/modal/alert-modal';
import { MoreHorizontal, Eye, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDeleteCategory } from '@/services/mapdata/queries';
import { showToast } from '@/components/ui/show-toast';

interface CellActionProps {
  data: any;
}

export const CellAction: React.FC<CellActionProps> = ({ data }) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  const deleteCategoryMutation = useDeleteCategory(
    () => {
      showToast('success', 'Category deleted successfully');
      setShowDeleteModal(false);
    },
    (error) => {
      showToast('error', error.response.data.message || 'Failed to delete category');
      console.error('Delete error:', error);
    }
  );

  const handleViewAll = () => {
    setIsSheetOpen(true);
  };

  const handleEdit = () => {
    navigate(`/dashboard/data/${data.id}/edit`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCategoryMutation.mutateAsync(data.id);
    } catch (error) {
      // Error is handled in the mutation callbacks
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleViewAll}>
            <Eye className="mr-2 h-4 w-4" />
            View All
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEdit}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setShowDeleteModal(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* View All Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{data.name}</SheetTitle>
          </SheetHeader>
          <div className="h-full overflow-y-auto">
            <div className="flex flex-wrap gap-2 py-4">
              {Array.isArray(data.tags) && data.tags.length > 0 ? (
                data.tags.map((tag: string, index: number) => (
                  <Badge
                    key={`tag-${index}`}
                    variant="secondary"
                    className="rounded-full px-3 py-2 text-sm font-normal"
                  >
                    {tag}
                  </Badge>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">
                  No data available
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Modal */}
      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title="Delete Category"
        description={`Are you sure you want to delete "${data.name}"? This action cannot be undone.`}
      />
    </>
  );
};